import React, { memo, useMemo, useRef } from 'react'
import { AnimatePresence, LayoutGroup, motion, cubicBezier } from 'framer-motion'
import { Flex } from '@chilipiper/design-system'
import {
  EnterPiperAiChatBlock,
  ReplyButtonsBlock as ReplyButtonsBlockType,
} from '../../../chat-data/types'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { StyledReply } from './styles'

interface Props {
  block: ReplyButtonsBlockType | EnterPiperAiChatBlock
  onReplyButtonClick: (index: number) => void
  isLastBlock: boolean
}

const easing = cubicBezier(0.42, 0, 1, 1)

export const ReplyButtonsBlock = memo(({ block, onReplyButtonClick, isLastBlock }: Props) => {
  const { isSessionExpired, blocks } = useMessageFeedContext()
  const isNoneSelected = block.selected === undefined
  const isButtonSelected = (index: number) => block.selected === index
  const replies = 'replies' in block ? block.replies : block.shortcuts
  const hasDispatchedRef = useRef(false)

  const firstReplyBlockId = useMemo(() => {
    return blocks.find(b => b.type === 'ReplyButtons' || b.type === 'EnterPiperAiChat')?.id
  }, [blocks])

  return (
    <LayoutGroup>
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        transition={{ delay: 0.1 }}
        style={{ width: '100%' }}
      >
        <Flex justifyContent='end' flexWrap='wrap' gap={2}>
          <AnimatePresence mode='popLayout'>
            {replies?.map((reply, index) => {
              if (isButtonSelected(index) || isNoneSelected) {
                return (
                  <motion.div
                    key={index}
                    exit={{ opacity: 0 }}
                    layoutId={isLastBlock ? `reply-button-${block.id}-${index}` : undefined}
                    transition={{
                      duration: 0.15,
                      ease: easing,
                    }}
                  >
                    <StyledReply
                      as='button'
                      data-test-id='WidgetReplyButtonsBlock'
                      type='button'
                      onClick={() => {
                        if (!hasDispatchedRef.current && block.id === firstReplyBlockId) {
                          window.dispatchEvent(
                            new CustomEvent(chiliChatEventName, {
                              detail: { action: chatPublicEventAction.CHAT_RESPONDED },
                            })
                          )
                          hasDispatchedRef.current = true
                        }
                        onReplyButtonClick(index)
                      }}
                      disabled={isButtonSelected(index) || isSessionExpired}
                      px={isNoneSelected ? 'xs' : 4}
                      py={isNoneSelected ? '5px' : '14px'}
                      textStyle={isButtonSelected(index) ? 'body-multiline' : 'multiline-button'}
                    >
                      {reply.text}
                    </StyledReply>
                  </motion.div>
                )
              }
            })}
          </AnimatePresence>
        </Flex>
      </motion.div>
    </LayoutGroup>
  )
})

ReplyButtonsBlock.displayName = 'ReplyButtonsBlock'
